import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function ContactDetail() {
    const { id } = useParams();
    const [c, setC] = useState(null);
    const [opps, setOpps] = useState([]);
    const [acts, setActs] = useState([]);
    const [newAct, setNewAct] = useState({ kind: "note", title: "", body: "" });

    const load = async () => {
        const [cr, or, ar] = await Promise.all([
            api.get(`/contacts/${id}`),
            api.get(`/opportunities?contact_id=${id}`),
            api.get(`/activities?contact_id=${id}`),
        ]);
        setC(cr.data); setOpps(or.data); setActs(ar.data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const addActivity = async (e) => {
        e.preventDefault();
        try {
            await api.post("/activities", { ...newAct, related_contact_id: id });
            setNewAct({ kind: "note", title: "", body: "" });
            toast.success("Activity added");
            load();
        } catch { toast.error("Failed"); }
    };

    if (!c) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="p-6 md:p-10" data-testid="page-contact-detail">
            <Link to="/app/contacts" className="text-xs text-muted-foreground hover:opacity-70">← Back to contacts</Link>
            <div className="overline mt-4">{c.contact_type}</div>
            <h1 className="mt-2 font-display text-4xl tracking-tight leading-none">{c.first_name} {c.last_name}</h1>
            {c.company && <div className="mt-2 text-sm text-muted-foreground">{c.company}</div>}

            <div className="mt-8 grid md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-8">
                    <section>
                        <div className="overline mb-3">Activity timeline</div>
                        <form onSubmit={addActivity} className="border border-border p-4 mb-4 space-y-3" data-testid="add-activity-form">
                            <div className="flex gap-2">
                                <select value={newAct.kind} onChange={(e) => setNewAct({ ...newAct, kind: e.target.value })} className="border border-border bg-background px-2 py-2 text-sm" data-testid="activity-kind">
                                    {["note", "call", "email", "meeting", "task", "document"].map((k) => <option key={k}>{k}</option>)}
                                </select>
                                <Input value={newAct.title} onChange={(e) => setNewAct({ ...newAct, title: e.target.value })} placeholder="Title" required className="flex-1" data-testid="activity-title" />
                            </div>
                            <Textarea value={newAct.body} onChange={(e) => setNewAct({ ...newAct, body: e.target.value })} placeholder="Body" rows={2} data-testid="activity-body" />
                            <button type="submit" className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="activity-submit">Add</button>
                        </form>
                        <div className="space-y-3">
                            {acts.length === 0 && <div className="text-sm text-muted-foreground">No activity yet.</div>}
                            {acts.map((a) => (
                                <div key={a.id} className="border border-border p-4" data-testid={`activity-${a.id}`}>
                                    <div className="flex items-center justify-between">
                                        <div className="overline">{a.kind}</div>
                                        <div className="text-xs text-muted-foreground">{new Date(a.occurred_at).toLocaleString()}</div>
                                    </div>
                                    <div className="mt-2 font-medium text-sm">{a.title}</div>
                                    {a.body && <div className="mt-2 text-sm text-muted-foreground whitespace-pre-line">{a.body}</div>}
                                </div>
                            ))}
                        </div>
                    </section>
                </div>

                <div className="space-y-8">
                    <section>
                        <div className="overline mb-3">Details</div>
                        <div className="border border-border p-4 space-y-2 text-sm">
                            <Row label="Email" value={c.email} />
                            <Row label="Phone" value={c.phone} />
                            <Row label="Company" value={c.company} />
                            <Row label="Address" value={c.address} />
                            <Row label="Source" value={c.source} />
                        </div>
                    </section>
                    <section>
                        <div className="overline mb-3">Opportunities</div>
                        <div className="space-y-2" data-testid="contact-opportunities">
                            {opps.length === 0 && <div className="text-sm text-muted-foreground">No opportunities.</div>}
                            {opps.map((o) => (
                                <div key={o.id} className="border border-border p-3 text-sm">
                                    <div className="font-medium">{o.name}</div>
                                    <div className="text-xs text-muted-foreground">{o.pipeline_key} · {o.stage_key}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-start justify-between gap-4 border-b border-border pb-2 last:border-b-0">
            <div className="overline">{label}</div>
            <div className="text-right flex-1">{value || "—"}</div>
        </div>
    );
}
